import { useCallback } from 'react';
import { aiAPI, aiAgentsAPI } from '../../services/api';
import toast from '../../services/toast';
import { formatNavigationScopeLabel } from '../../utils/aiNavigationContext';
import { loadRecentEntities, pushRecentEntity } from '../../utils/aiRecentEntities';
import { isQuickModeOrgWideMessage } from '../../utils/aiQuickModeScope';
import { INDICATOR_TYPES } from './chatIndicatorTypes';
import {
  shouldInsertScopeChange,
  deriveScopeKindFromScope,
  createScopeChangeMessage,
  insertScopeChangeBeforeMessage,
} from './aiChatScopeChange';
import useAIChatStream from './useAIChatStream';
import { getErrorMessage } from '../../utils/errors';
import { getAiChatErrorPresentation, isAiSpendCapError } from '../../utils/aiChatErrors';
import { VANTI_NOT_CONFIGURED_MESSAGE } from '../../utils/aiSetupHelp';

/**
 * Send/stop/retry orchestration for Vanti chat (streaming + fallback API).
 */
export function useAIChatSend({
  input,
  setInput,
  messages,
  setMessages,
  loading,
  setLoading,
  configured,
  disabledByOrg,
  setDisabledByOrg,
  chatMode,
  selectedAgentId,
  threadId,
  setThreadId,
  threads,
  effectiveNavigationContext,
  scopeChip,
  setScopeChip,
  setScopeOverride,
  setIndexHint,
  currentThreadTitle,
  setCurrentThreadTitle,
  setThreads,
  loadThreads,
  setCooldownUntil,
  lastSendPayload,
  setLastSendPayload,
  setStreamingMessageId,
  setStreamingStatus,
  onCooldown,
  abortControllerRef,
}: any) {
  const buildChatBody = (
    messageList,
    { useThreadId = threadId, forceNewThread = false, navContext = null } = {}
  ) => {
    const body: any = {
      messages: messageList
        .filter((m) => !INDICATOR_TYPES.has(m.type))
        .map((m) => ({ role: m.role, content: m.content })),
      mode: chatMode,
    };
    if (selectedAgentId) {
      body.agentId = selectedAgentId;
    }
    if (!forceNewThread && useThreadId) {
      body.threadId = useThreadId;
    } else {
      body.createThread = true;
    }
    const navCtx = navContext || effectiveNavigationContext;
    const lastUserMessage =
      [...messageList].reverse().find((m) => m.role === 'user')?.content || '';
    const omitNavScope =
      (chatMode === 'quick' || chatMode === 'auto') && isQuickModeOrgWideMessage(lastUserMessage);
    if (navCtx && !omitNavScope) {
      body.navigationContext = navCtx;
    }
    const recent = loadRecentEntities();
    if (recent.length) body.recentEntityIds = recent;
    return body;
  };

  const applyChatResponse = (res: any, { hadThreadId, replaceMessageId, originalQuestion }: any = {}) => {
    const {
      reply,
      quickActions,
      threadId: nextThreadId,
      scope,
      meta,
      charts,
      vegaCharts,
      tables,
      summaries,
      questions,
      memoryUsed,
      ragSources,
      followUps,
    } = res;
    const turnId = res.turnId || meta?.turnId;
    if (nextThreadId && (!hadThreadId || nextThreadId !== hadThreadId)) {
      setThreadId(nextThreadId);
      localStorage.setItem('vanti.threadId', nextThreadId);
    }
    if (nextThreadId && (!hadThreadId || !currentThreadTitle)) {
      aiAgentsAPI
        .generateThreadTitle(nextThreadId)
        .then((data) => {
          if (data?.title) {
            setCurrentThreadTitle(data.title);
            setThreads((prev) =>
              prev.map((t) => (t.id === nextThreadId ? { ...t, title: data.title } : t))
            );
            loadThreads();
          }
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.warn('[Vanti] generateThreadTitle failed:', err);
          }
        });
    }
    if (scope) {
      setScopeChip(formatNavigationScopeLabel(scope));
      if (scope.projectId)
        pushRecentEntity({ kind: 'project', id: scope.projectId, label: scope.projectName });
    }
    if (meta?.indexStatus?.summary) {
      setIndexHint(meta.indexStatus.summary);
    } else if (meta?.indexStatus && typeof meta.indexStatus === 'string') {
      setIndexHint(meta.indexStatus);
    }
    const clarification = meta?.clarification;
    const scopePicks = (quickActions || []).filter((a) => a.type === 'scope_pick');
    const linkActions = (quickActions || []).filter((a) => a.type !== 'scope_pick');

    const assistantMsg = {
      role: 'assistant',
      content: reply,
      id: turnId || `assistant-${Date.now()}`,
      turnId: turnId || undefined,
      quickActions: linkActions,
      charts: charts || [],
      vegaCharts: vegaCharts || [],
      tables: tables || [],
      summaries: summaries || [],
      questions: questions || [],
      scopePicks: scopePicks.length ? scopePicks : clarification,
      clarificationOnly: Boolean(clarification?.length && !scopePicks.length),
      scopeResolved: false,
      originalQuestion: originalQuestion || undefined,
      streaming: false,
      memoryUsed: Boolean(memoryUsed),
      ragSources: Array.isArray(ragSources) && ragSources.length ? ragSources : [],
      followUps: Array.isArray(followUps) ? followUps : [],
    };

    const scopeLabel = scope ? formatNavigationScopeLabel(scope) : '';

    if (replaceMessageId) {
      setMessages((prev) => {
        const scopeChangeMsg =
          scope &&
          scopeLabel &&
          scopeLabel !== scopeChip &&
          shouldInsertScopeChange(prev, scopeLabel)
            ? createScopeChangeMessage({
                kind: deriveScopeKindFromScope(scope),
                label: scopeLabel,
              })
            : null;
        const replaced = prev.map((m) =>
          m.id === replaceMessageId ? { ...assistantMsg, content: reply || m.content } : m
        );
        return scopeChangeMsg
          ? insertScopeChangeBeforeMessage(replaced, scopeChangeMsg, replaceMessageId)
          : replaced;
      });
    } else {
      setMessages((prev) => {
        const scopeChangeMsg =
          scope &&
          scopeLabel &&
          scopeLabel !== scopeChip &&
          shouldInsertScopeChange(prev, scopeLabel)
            ? createScopeChangeMessage({
                kind: deriveScopeKindFromScope(scope),
                label: scopeLabel,
              })
            : null;
        const next = scopeChangeMsg ? [...prev, scopeChangeMsg] : prev;
        return [...next, assistantMsg];
      });
    }
    setStreamingMessageId(null);
    setStreamingStatus('');
  };

  const { streamChatTurn } = useAIChatStream({
    setStreamingStatus,
    setMessages,
    applyChatResponse,
  });

  const handleChatError = (err, hadThreadId) => {
    if (err && err.code === 'FEATURE_DISABLED') {
      setDisabledByOrg(true);
      toast.error('AI chat is disabled for your organisation.');
      setMessages((prev) => prev.slice(0, -1));
      return true;
    }
    if (err && err.code === 'NOT_FOUND' && hadThreadId) {
      return 'retry_new_thread';
    }
    const presentation = getAiChatErrorPresentation(err as Error & { code?: string });
    if (presentation.applyCooldown) {
      const retryAfterMs =
        Number(err?.meta?.retryAfterMs) || (err.code === 'AI_DUPLICATE' ? 1000 : 5000);
      setCooldownUntil(Date.now() + Math.max(500, retryAfterMs));
    }

    setMessages((prev) => {
      const withoutStream = prev.filter((m) => !m.streaming);
      return [
        ...withoutStream,
        {
          id: `failed-${Date.now()}`,
          type: 'message_failed',
          errorMessage: presentation.label,
          errorSublabel: presentation.sublabel,
          errorCode: err?.code,
          retryPayload: presentation.showRetry ? lastSendPayload : undefined,
        },
      ];
    });
    return true;
  };

  const send = async (overrideText?: any, externalScope?: any) => {
    const text = (overrideText || input || '').trim();
    if (!text || loading || onCooldown) return;
    if (disabledByOrg) {
      toast.error('AI chat is disabled for your organisation.');
      return;
    }
    if (configured === false) {
      toast.error(VANTI_NOT_CONFIGURED_MESSAGE);
      return;
    }

    if (!overrideText) setInput('');

    const navCtx = externalScope || effectiveNavigationContext;
    if (externalScope) {
      setScopeOverride(externalScope);
    }

    const userMsg = { role: 'user', content: text, id: `user-${Date.now()}` };
    const isFirstMessage = messages.length === 0;
    const prefixIndicators =
      isFirstMessage && threads.length > 0
        ? [{ id: `newconv-${Date.now()}`, type: 'new_conversation' }]
        : [];
    const next = [...messages, ...prefixIndicators, userMsg];
    setMessages(next);
    setLoading(true);
    setStreamingStatus('');

    const hadThreadId = threadId;
    let body = buildChatBody(next, { navContext: navCtx });
    setLastSendPayload(body);

    const streamMsgId = `stream-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: streamMsgId, role: 'assistant', content: '', streaming: true },
    ]);
    setStreamingMessageId(streamMsgId);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const streamResult = await streamChatTurn({
        payload: body,
        hadThreadId,
        streamMessageId: streamMsgId,
        originalQuestion: text,
        abortController,
      });
      if (streamResult.aborted) {
        setStreamingMessageId(null);
        setStreamingStatus('');
        setLoading(false);
        return;
      }
      if (streamResult.ok) return;
      if (streamResult.error && isAiSpendCapError(streamResult.error)) {
        handleChatError(streamResult.error, hadThreadId);
        return;
      }

      try {
        const res = await aiAPI.chat(body);
        applyChatResponse(res, {
          hadThreadId,
          replaceMessageId: streamMsgId,
          originalQuestion: text,
        });
      } catch (err) {
        const retry = handleChatError(err, hadThreadId);
        if (retry === 'retry_new_thread') {
          setThreadId('');
          localStorage.removeItem('vanti.threadId');
          body = buildChatBody(next, { useThreadId: '', forceNewThread: true, navContext: navCtx });
          setLastSendPayload(body);
          try {
            const res = await aiAPI.chat(body);
            applyChatResponse(res, { hadThreadId: '', originalQuestion: text });
          } catch (retryErr) {
            handleChatError(retryErr, '');
          }
        }
      }
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
      setLoading(false);
      setStreamingMessageId(null);
      setStreamingStatus('');
    }
  };

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setStreamingMessageId(null);
    setStreamingStatus('');
    setLoading(false);
  }, [abortControllerRef, setLoading, setStreamingMessageId, setStreamingStatus]);

  const retryLast = async (payloadOverride) => {
    let payload = payloadOverride || lastSendPayload;
    if (!payload || loading || onCooldown) return;
    setLoading(true);
    setStreamingStatus('');
    const hadThreadId = threadId;
    const lastUserMsg = [...(payload.messages || [])].reverse().find((m) => m.role === 'user');
    const originalQuestion = lastUserMsg?.content || '';

    const retryStreamId = `stream-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: retryStreamId, role: 'assistant', content: '', streaming: true },
    ]);
    setStreamingMessageId(retryStreamId);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const streamResult = await streamChatTurn({
        payload,
        hadThreadId,
        streamMessageId: retryStreamId,
        originalQuestion,
        abortController,
      });
      if (streamResult.aborted) {
        return;
      }
      if (streamResult.ok) return;
      if (streamResult.error && isAiSpendCapError(streamResult.error)) {
        handleChatError(streamResult.error, hadThreadId);
        return;
      }

      try {
        const res = await aiAPI.chat(payload);
        applyChatResponse(res, { hadThreadId, replaceMessageId: retryStreamId, originalQuestion });
      } catch (err) {
        if (err && (err as any).code === 'NOT_FOUND' && payload.threadId) {
          setThreadId('');
          localStorage.removeItem('vanti.threadId');
          payload = { ...payload };
          delete payload.threadId;
          payload.createThread = true;
          setLastSendPayload(payload);
          try {
            const res = await aiAPI.chat(payload);
            applyChatResponse(res, {
              hadThreadId: '',
              replaceMessageId: retryStreamId,
              originalQuestion,
            });
            setLastSendPayload(null);
            return;
          } catch (retryErr) {
            toast.error(getErrorMessage(retryErr, 'Failed to get AI response'));
            return;
          }
        }
        if (
          err &&
          ((err as any).code === 'AI_RATE_LIMIT' || (err as any).code === 'AI_DUPLICATE' || (err as any).code === 'AI_TIMEOUT')
        ) {
          const retryAfterMs =
            Number((err as any)?.meta?.retryAfterMs) || ((err as any).code === 'AI_DUPLICATE' ? 1000 : 5000);
          setCooldownUntil(Date.now() + Math.max(500, retryAfterMs));
        }
        handleChatError(err, hadThreadId);
      }
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
      setLoading(false);
      setStreamingMessageId(null);
      setStreamingStatus('');
    }
  };

  return {
    buildChatBody,
    applyChatResponse,
    streamChatTurn,
    handleChatError,
    send,
    retryLast,
    handleStop,
  };
}
